// Campaigns: the list and one campaign (owner, admin, viewer), drafts and the
// live audience count (owner and admin). The server enforces the roles.
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from './client'
import type { components } from './generated/schema'

export type Campaign = components['schemas']['Campaign']
export type CampaignPage = components['schemas']['CampaignPage']
export type CampaignInput = components['schemas']['CampaignInput']
export type CampaignVariable = components['schemas']['CampaignVariable']
export type CampaignAudienceInput = components['schemas']['CampaignAudienceInput']
export type AudienceCount = components['schemas']['AudienceCount']
export type CampaignStatus = components['schemas']['CampaignStatus']
export type CampaignEstimate = components['schemas']['CampaignEstimate']
export type CampaignProgress = components['schemas']['CampaignProgress']
export type CampaignHold = components['schemas']['CampaignHold']
export type RecipientStatus = components['schemas']['RecipientStatus']
export type CampaignRecipient = components['schemas']['CampaignRecipient']
export type CampaignRecipientPage = components['schemas']['CampaignRecipientPage']

const keys = {
  all: ['campaigns'] as const,
  list: ['campaigns', 'list'] as const,
  one: (id: string) => ['campaigns', 'one', id] as const,
  audience: (a: CampaignAudienceInput) => ['campaigns', 'audience', a] as const,
  recipients: (id: string, status: RecipientStatus) => ['campaigns', 'recipients', id, status] as const,
}

// A campaign that is scheduled or sending changes by itself; it is read
// again every few seconds while open.
const running = (c: Campaign | undefined) => c?.status === 'scheduled' || c?.status === 'sending'

export function useCampaigns() {
  return useInfiniteQuery({
    queryKey: keys.list,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<CampaignPage> =>
      unwrap(await api.GET('/campaigns', { params: { query: pageParam ? { cursor: pageParam } : {} } })),
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: (q) => (q.state.data?.pages.some((p) => p.items.some(running)) ? 10_000 : false),
  })
}

export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: keys.one(id ?? ''),
    enabled: id !== undefined,
    queryFn: async (): Promise<Campaign> =>
      unwrap(await api.GET('/campaigns/{campaignId}', { params: { path: { campaignId: id ?? '' } } })),
    refetchInterval: (q) => (running(q.state.data) ? 5_000 : false),
  })
}

// The count follows the form as tags change; the last count stays on screen
// while the next one loads, so the numbers do not flicker.
export function useAudienceCount(a: CampaignAudienceInput, enabled: boolean) {
  return useQuery({
    queryKey: keys.audience(a),
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<AudienceCount> => unwrap(await api.POST('/campaign-audience', { body: a })),
  })
}

export function useSaveCampaign(id: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CampaignInput): Promise<Campaign> =>
      id
        ? unwrap(await api.PUT('/campaigns/{campaignId}', { params: { path: { campaignId: id } }, body }))
        : unwrap(await api.POST('/campaigns', { body })),
    onSuccess: (c) => {
      qc.setQueryData(keys.one(c.id), c)
      void qc.invalidateQueries({ queryKey: keys.list })
    },
  })
}

export function useDeleteCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/campaigns/{campaignId}', { params: { path: { campaignId: id } } })
      if (!res.response.ok) unwrap(res)
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.all }),
  })
}

type Action = 'launch' | 'unschedule' | 'cancel'

// Launch, unschedule and cancel all answer with the campaign after the change.
export function useCampaignAction(id: string, action: Action) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<Campaign> => {
      const params = { params: { path: { campaignId: id } } }
      switch (action) {
        case 'launch':
          return unwrap(await api.POST('/campaigns/{campaignId}/launch', params))
        case 'unschedule':
          return unwrap(await api.POST('/campaigns/{campaignId}/unschedule', params))
        case 'cancel':
          return unwrap(await api.POST('/campaigns/{campaignId}/cancel', params))
      }
    },
    onSuccess: (c) => {
      qc.setQueryData(keys.one(c.id), c)
      void qc.invalidateQueries({ queryKey: keys.list })
    },
  })
}

export function useSendCampaignTest(id: string) {
  return useMutation({
    mutationFn: async (body: { contactId: string; clientMessageId: string }) =>
      unwrap(await api.POST('/campaigns/{campaignId}/test-messages', { params: { path: { campaignId: id } }, body })),
  })
}

export function useCampaignRecipients(id: string, status: RecipientStatus, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: keys.recipients(id, status),
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<CampaignRecipientPage> =>
      unwrap(
        await api.GET('/campaigns/{campaignId}/recipients', {
          params: { path: { campaignId: id }, query: { status, ...(pageParam ? { cursor: pageParam } : {}) } },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor,
  })
}
